import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  InputNumber,
  Spin,
  message,
  Select,
  Tooltip,
  Input,
  Upload,
  Image,
} from "antd";
import {
  InfoCircleOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import imageCompression from "browser-image-compression";
import { CSSTransition, TransitionGroup } from "react-transition-group";

const { Option } = Select;
const { TextArea } = Input;

interface FoodModalProps {
  open: boolean;
  onClose: () => void;
  action: { type: string; data?: any } | null;
  formData: {
    name: string;
    price: string;
    category: string;
    description: string;
    attributes: string[];
    images: string[];
    product_url?: string; // Nueva propiedad para la URL directa del producto
  };
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  handleUpdate: () => Promise<boolean>;
  handleSave: () => Promise<boolean>;
  categories: string[];
  user_data: any;
}

const FoodModal: React.FC<FoodModalProps> = ({
  open,
  onClose,
  action,
  formData,
  handleChange,
  handleUpdate,
  handleSave,
  categories,
  user_data,
}) => {
  const [loading, setLoading] = useState(false);
  // Estado local para las imágenes (previsualización)
  const [imageList, setImageList] = useState<string[]>(formData.images || []);
  // Estado para el input de URL directa para imágenes (solo para e-commerce)
  const [imageUrl, setImageUrl] = useState("");

  // Actualiza el estado local cuando cambie formData.images
  useEffect(() => {
    setImageList(formData.images || []);
  }, [formData.images]);

  // Función para obtener los placeholders personalizados según el establecimiento.
  const getPlaceholders = () => {
    const establishment = user_data["custom:establishment"];
    switch (establishment) {
      case "restaurant":
        return {
          name: "Ej: Plato Principal",
          price: "Ej: 25000",
          category: "Ej: Entradas, Postres, Bebidas",
          attributes: "Ej: Picante, Sin Gluten",
          description: "Ej: Plato elaborado con ingredientes frescos",
        };
      case "bar":
        return {
          name: "Ej: Bebida",
          price: "Ej: 10000",
          category: "Ej: Cócteles, Cervezas",
          attributes: "Ej: Dulce, Fuerte, Amargo",
          description: "Ej: Bebida refrescante y de calidad",
        };
      case "nightclub":
        return {
          name: "Ej: Producto",
          price: "Ej: 50000",
          category: "Ej: Cócteles, Cervezas",
          attributes: "Ej: Dulce, Fuerte, Amargo",
          description: "Ej: Producto de alta demanda",
        };
      case "medical":
        return {
          name: "Ej: Consulta",
          price: "Ej: 100000",
          category: "Ej: Especialidades Médicas",
          attributes: "Ej: Redacción, Negociación, Comunicación",
          description: "Ej: Consulta médica de alta calidad",
        };
      case "e-commerce":
        return {
          name: "Ej: Producto",
          price: "Ej: 100000",
          category: "Ej: Categoría",
          attributes: "Ej: Gama alta, Gama baja, Color, Talla",
          description: "Ej: Producto con garantía",
        };
      default:
        return {
          name: "Ej: Producto",
          price: "Ej: 100000",
          category: "Ej: Categoría",
          attributes: "Ej: Atributos",
          description: "Ej: Descripción del producto",
        };
    }
  };

  const placeholders = getPlaceholders();

  // Función auxiliar para convertir un archivo a base64
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Antes de subir, se comprime la imagen, se convierte a base64 y se actualiza
  // tanto el estado local como el formData (campo "images").
  const beforeUpload = async (file: File) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Solo se pueden subir imágenes.");
      return Upload.LIST_IGNORE;
    }
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 1,
      };
      const compressedFile = await imageCompression(file, options);
      const base64 = await getBase64(compressedFile);
      setImageList((prev) => {
        const newList = [...prev, base64];
        // Actualiza el formData con las imágenes en base64.
        handleChange({
          target: { id: "images", value: newList },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        return newList;
      });
    } catch (error) {
      message.error("Error al comprimir la imagen.");
      console.error(error);
    }
    // Retornamos false para prevenir la carga automática.
    return false;
  };

  // Función para eliminar una imagen y actualizar el formData.
  const handleRemoveImage = (indexToRemove: number) => {
    setImageList((prev) => {
      const newList = prev.filter((_, index) => index !== indexToRemove);
      handleChange({
        target: { id: "images", value: newList },
      } as unknown as React.ChangeEvent<HTMLInputElement>);
      return newList;
    });
  };

  // Maneja los cambios en el input para la URL directa de imágenes.
  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(e.target.value);
  };

  // Agrega la URL ingresada a la lista de imágenes (sin reprocesarla)
  const handleAddImageUrl = () => {
    if (!imageUrl.trim()) {
      message.error("Por favor, ingrese una URL válida.");
      return;
    }
    if (!imageUrl.startsWith("http://") && !imageUrl.startsWith("https://")) {
      message.error("La URL debe comenzar con http:// o https://");
      return;
    }
    setImageList((prev) => {
      const newList = [...prev, imageUrl.trim()];
      handleChange({
        target: { id: "images", value: newList },
      } as unknown as React.ChangeEvent<HTMLInputElement>);
      return newList;
    });
    setImageUrl("");
  };

  const handleActionClick = async () => {
    // Solo se validan los campos obligatorios: nombre, precio y categoría.
    if (!formData.name || !formData.price || !formData.category) {
      message.error("El nombre, el precio y la categoría son obligatorios");
      return;
    }
    // Al enviar, formData ya contiene los campos "images", "attributes" y "product_url".
    setLoading(true);
    if (action?.type === "edit") {
      await handleUpdate();
    } else {
      await handleSave();
    }
    setLoading(false);
  };

  return (
    <Modal
      title={action?.type === "edit" ? "Editar Producto" : "Crear Producto"}
      open={open}
      maskClosable={false}
      onCancel={() => !loading && onClose()}
      footer={[
        <Button key="back" onClick={() => !loading && onClose()} disabled={loading}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={handleActionClick} loading={loading}>
          {action?.type === "edit" ? "Actualizar" : "Guardar"}
        </Button>,
      ]}
    >
      {/* CSS para las animaciones */}
      <style>
        {`
          .fade-enter {
            opacity: 0;
            transform: scale(0.95);
          }
          .fade-enter-active {
            opacity: 1;
            transform: scale(1);
            transition: opacity 300ms, transform 300ms;
          }
          .fade-exit {
            opacity: 1;
            transform: scale(1);
          }
          .fade-exit-active {
            opacity: 0;
            transform: scale(0.95);
            transition: opacity 300ms, transform 300ms;
          }
        `}
      </style>
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16 }}>
          <label>
            Nombre del Producto <span style={{ color: "red" }}>*</span>{" "}
            <Tooltip title="Ingrese el nombre del producto">
              <InfoCircleOutlined />
            </Tooltip>
          </label>
          <Input
            id="name"
            placeholder={placeholders.name}
            value={formData?.name}
            onChange={handleChange}
            style={{ width: "100%" }}
          />
        </div>

        {/* Solo para e-commerce: Campo para la URL directa del producto */}
        {user_data["custom:establishment"] === "e-commerce" && (
          <div style={{ marginBottom: 16 }}>
            <label>
              URL Directa del Producto{" "}
              <Tooltip title="Ingrese la URL directa del producto. Este enlace redirige a la página del producto en otro sitio.">
                <InfoCircleOutlined />
              </Tooltip>
            </label>
            <Input
              id="product_url"
              placeholder="Ingrese la URL directa del producto"
              value={formData?.product_url || ""}
              onChange={handleChange}
              style={{ width: "100%" }}
            />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label>
            Precio <span style={{ color: "red" }}>*</span>{" "}
            <Tooltip title={`Ingrese el precio en pesos colombianos (${placeholders.price})`}>
              <InfoCircleOutlined />
            </Tooltip>
          </label>
          <InputNumber
            id="price"
            placeholder={placeholders.price}
            value={formData?.price ? parseFloat(formData.price) : undefined}
            onChange={(value) =>
              handleChange({
                target: { id: "price", value: value?.toString() || "" },
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            style={{ marginBottom: 10, width: "100%" }}
            formatter={(value) =>
              value ? `COP ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""
            }
            parser={(value: string | undefined) =>
              value ? Number(value.replace(/[^\d]/g, "")) : 0
            }
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label>
            Categoría <span style={{ color: "red" }}>*</span>{" "}
            <Tooltip title={`Seleccione la categoría (${placeholders.category})`}>
              <InfoCircleOutlined />
            </Tooltip>
          </label>
          <Select
            id="category"
            placeholder={placeholders.category}
            value={formData?.category || undefined}
            onChange={(value) =>
              handleChange({
                target: { id: "category", value },
              } as unknown as React.ChangeEvent<HTMLSelectElement>)
            }
            style={{ marginBottom: 10, width: "100%" }}
          >
            {categories.map((category) => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        </div>

        {/* Campo de atributos (tags) - Opcional */}
        <div style={{ marginBottom: 16 }}>
          <label>
            Características{" "}
            <Tooltip title={`Ingrese las características del producto/servicio y presione Enter (${placeholders.attributes})`}>
              <InfoCircleOutlined />
            </Tooltip>
          </label>
          <Select
            id="attributes"
            mode="tags"
            placeholder={placeholders.attributes}
            tokenSeparators={[","]}
            value={formData?.attributes || []}
            onChange={(value: string[]) =>
              handleChange({
                target: { id: "attributes", value },
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            style={{ marginBottom: 10, width: "100%" }}
          />
        </div>

        {/* Campo de descripción - Opcional */}
        <div style={{ marginBottom: 16 }}>
          <label>
            Descripción{" "}
            <Tooltip title={`Ingrese una descripción del producto/servicio (${placeholders.description})`}>
              <InfoCircleOutlined />
            </Tooltip>
          </label>
          <TextArea
            id="description"
            placeholder={placeholders.description}
            value={formData?.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e)}
            style={{ marginBottom: 10 }}
            rows={4}
          />
        </div>

        {/* Sección de imágenes - Opcional */}
        <div style={{ marginBottom: 16 }}>
          <label>
            Imágenes{" "}
            <Tooltip title="Suba imágenes del producto para su previsualización o, en caso de e-commerce, ingrese la URL directa de la imagen si ya está alojada en otro sitio.">
              <InfoCircleOutlined />
            </Tooltip>
            <br />
          </label>
          <Upload
            listType="picture"
            beforeUpload={beforeUpload}
            multiple
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Subir Imagen</Button>
          </Upload>
          {/* Para establecimientos e-commerce: agregar URL directa de la imagen */}
          {/* {user_data["custom:establishment"] === "e-commerce" && (
            <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
              <Tooltip title="Ingrese la URL directa de la imagen del producto, si ya está alojada en otro sitio.">
                <InfoCircleOutlined style={{ marginRight: 5 }} />
              </Tooltip>
              <Input
                placeholder="Ingrese URL directa de la imagen"
                value={imageUrl}
                onChange={handleImageUrlChange}
                style={{ flex: 1 }}
              />
              <Button onClick={handleAddImageUrl}>Agregar URL</Button>
            </div>
          )} */}
          <br />
          <TransitionGroup style={{ display: "flex", gap: 10, marginTop: 10 }}>
            {imageList.map((img, index) => (
              <CSSTransition key={index} timeout={300} classNames="fade">
                <div style={{ position: "relative", display: "inline-block" }}>
                  <Image
                    width={80}
                    height={80}
                    src={img}
                    style={{ borderRadius: 5 }}
                  />
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveImage(index)}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: "50%",
                      padding: "2px",
                      margin: "2px",
                    }}
                  />
                </div>
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </Spin>
    </Modal>
  );
};

export default FoodModal;